import { NgModule } from '@angular/core';
import { angularProvidersBuilder } from '@harmony/angular';
import { Auth, User as AuthUser } from 'firebase/auth';
import { Firestore } from 'firebase/firestore';
import { Functions } from 'firebase/functions';
import { FirebaseToken } from '../../app/core/firebase.module';
import { AuthDropboxProvider } from '../auth-dropbox/auth-dropbox.provider';
import { AuthGoogleProvider } from '../auth-google/auth-google.provider';
import { AuthMicrosoftProvider } from '../auth-microsoft/auth-microsoft.provider';
import { AuthZohoProvider } from '../auth-zoho/auth-zoho.provider';
import { CommonProvider } from '../common/common.provider';
import { AuthDefaultProvider, AuthProvider } from './auth.provider';
import { GetCredentialInteractor } from './domain/interactors/get-credential.interactor';
import { GetUserInteractor } from './domain/interactors/get-user.interactor';
import { IsLoggedInteractor } from './domain/interactors/is-logged.interactor';
import { LoginWithPopupInteractor } from './domain/interactors/login-with-popup.interactor';
import { LogoutInteractor } from './domain/interactors/logout.interactor';
import { RefreshCredentialInteractor } from './domain/interactors/refresh-credential.interactor';
import { GetUserByIdInteractor } from './domain/interactors/get-user-by-id.interactor';
import { Subject } from 'rxjs';
import {
  REFRESH_CREDENTIAL_ERROR$,
  USER_AUTH_STATE_CHANGED$,
  USER_GET_CURRENT$,
} from '../../app/core/core.module';
import { LoginWithRedirectInteractor } from './domain/interactors/login-with-redirect.interactor';
import { LoginFinishInteractor } from './domain/interactors/login-finish.interactor';
import { User } from '@uploadcam/common';
import { AnalyticsService } from '../../app/features/shared/services/analytics.service';
import { AuthenticateWithCredentialsInteractor } from './domain/interactors/authenticate-with-credentials.interactor';

@NgModule({
  providers: angularProvidersBuilder({
    provide: AuthProvider,
    deps: [
      AnalyticsService,
      FirebaseToken.Auth,
      AuthDropboxProvider,
      AuthGoogleProvider,
      AuthMicrosoftProvider,
      AuthZohoProvider,
      CommonProvider,
      FirebaseToken.Firestore,
      FirebaseToken.Functions,
      REFRESH_CREDENTIAL_ERROR$,
      USER_AUTH_STATE_CHANGED$,
      USER_GET_CURRENT$,
    ],
    useFactory: (
      analytics: AnalyticsService,
      auth: Auth,
      authDropbox: AuthDropboxProvider,
      authGoogle: AuthGoogleProvider,
      authMicrosoft: AuthMicrosoftProvider,
      authZoho: AuthZohoProvider,
      common: CommonProvider,
      firestore: Firestore,
      functions: Functions,
      refreshCredentialError$: Subject<void>,
      userAuthStateChanged$: Subject<AuthUser | undefined>,
      userGetCurrent$: Subject<User>,
    ) =>
      new AuthDefaultProvider(
        analytics,
        auth,
        authDropbox,
        authGoogle,
        authMicrosoft,
        authZoho,
        common,
        firestore,
        functions,
        refreshCredentialError$,
        userAuthStateChanged$,
        userGetCurrent$,
      ),
  })
    .add(AuthenticateWithCredentialsInteractor, (p) => p.provideAuthenticateWithCredentials())
    .add(GetCredentialInteractor, (p) => p.provideGetCredential())
    .add(GetUserByIdInteractor, (p) => p.provideGetUserById())
    .add(GetUserInteractor, (p) => p.provideGetUser())
    .add(IsLoggedInteractor, (p) => p.provideIsLogged())
    .add(LoginFinishInteractor, (p) => p.provideLoginFinish())
    .add(LoginWithPopupInteractor, (p) => p.provideLoginWithPopup())
    .add(LoginWithRedirectInteractor, (p) => p.provideLoginWithRedirect())
    .add(LogoutInteractor, (p) => p.provideLogout())
    .add(RefreshCredentialInteractor, (p) => p.provideRefreshCredential())
    .build(),
})
export class AuthProviderModule {}
